import type { ConnectionDto, OAuthCheckDto } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, ApiError, errorText } from '../api.ts';
import { BrandLockup } from '../components/Brand.tsx';
import { Threshold } from '../components/Threshold.tsx';
import { Button, ErrorLine, Loading } from '../components/ui.tsx';
import { dateWords } from '../days.ts';
import { useMe } from '../session.tsx';
import { answerAsk, checkUnreached, goesTo } from './claudeAsk.ts';

/**
 * The Allow page (spec "Claude Connector" §3.3; design study 28): where a staff member lets Claude read the portal as
 * them. Claude sends the browser here with its request in the address; the check asks the API whether it holds, and
 * Allow or Don't allow sends the browser on, to the return address the client registered and nowhere else.
 */
export function ConnectClaude() {
  const { data: me } = useMe();
  const search = window.location.search;
  const check = useQuery<OAuthCheckDto, ApiError>({
    queryKey: ['oauth', 'authorize', search],
    queryFn: () => api<OAuthCheckDto>(`/oauth/authorize${search}`),
    // A refused request is the answer, not a fault to try again; and nothing here changes while the page is open.
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const answer = async (allow: boolean) => {
    setBusy(true);
    setRefused(null);
    try {
      // Busy stays on once it is sent: the page is on its way to Claude's.
      window.location.href = await answerAsk(search, allow);
    } catch (e) {
      setRefused(errorText(e));
      setBusy(false);
    }
  };
  return (
    <Threshold>
      <ConnectClaudeView
        check={check.data ?? null}
        error={check.error ? errorText(check.error) : null}
        unreached={check.error ? checkUnreached(check.error) : false}
        name={me?.user.name || me?.user.email || ''}
        goesTo={goesTo(new URLSearchParams(search).get('redirect_uri') ?? '')}
        busy={busy}
        refused={refused}
        onAllow={() => void answer(true)}
        onDeny={() => void answer(false)}
      />
    </Threshold>
  );
}

export function ConnectClaudeView(p: {
  /** What the API said of the request; null until it has, or when it refused (`error`). */
  check: OAuthCheckDto | null;
  /** The API's words when the request does not hold, or when the check could not be made (`unreached`). */
  error: string | null;
  /** The check got a fault or no answer: the portal's trouble, to try again, not a link that does not work. */
  unreached?: boolean;
  /** Who is signed in. */
  name: string;
  goesTo: string;
  busy: boolean;
  /** The API's words when the answer did not go through; the buttons stay. */
  refused?: string | null;
  onAllow: () => void;
  onDeny: () => void;
}) {
  const { check, error } = p;
  return (
    <>
      <div className="mb-14" role="img" aria-label="Never Stop Building, build portal">
        <BrandLockup />
      </div>
      <p className="label">Connect Claude</p>
      <h1 className="mt-1.5 text-[2.5rem]">Claude wants to read your projects</h1>
      {error ? (
        <div className="mt-5">
          <p>{p.unreached ? 'The portal could not check this link. Try again.' : 'This link to connect Claude does not work.'}</p>
          <p className="meta mt-1">{error}</p>
        </div>
      ) : !check ? (
        <Loading />
      ) : !check.allowed ? (
        <p className="mt-5">Connecting Claude is for NSB’s staff for now.</p>
      ) : (
        <>
          <p className="mt-2 text-ink-2">{`As ${p.name}. Claude can read what you can see in the portal and changes nothing. You can revoke it on Account.`}</p>
          <div className="mt-6 border-y border-rule py-3">
            <div className="label">Goes to</div>
            <p className="mt-0.5">{p.goesTo}</p>
          </div>
          <ErrorLine error={p.refused} />
          <div className="mt-7 flex flex-wrap items-center gap-x-6">
            <Button disabled={p.busy} onClick={p.onAllow}>
              Allow
            </Button>
            <Button tone="quiet" disabled={p.busy} onClick={p.onDeny}>
              Don’t allow
            </Button>
          </div>
        </>
      )}
    </>
  );
}

/**
 * Account → Claude (design study 28, picks 3A and 4A): each connection of Claude to this person with Revoke, asked
 * first in the row; with none, the address to add the portal to Claude by.
 */
export function ClaudeConnections(p: { connections: ConnectionDto[]; origin: string; onRevoke: (id: string) => void; busy: boolean }) {
  const [asking, setAsking] = useState<string | null>(null);
  if (p.connections.length === 0) return <AddToClaude address={`${p.origin}/api/mcp`} />;
  return (
    <ul>
      {p.connections.map((c) => (
        <li key={c.id}>
          <ConnectionRow
            connection={c}
            asking={asking === c.id}
            busy={p.busy}
            onAsk={() => setAsking(c.id)}
            onKeep={() => setAsking(null)}
            onRevoke={() => {
              setAsking(null);
              p.onRevoke(c.id);
            }}
          />
        </li>
      ))}
    </ul>
  );
}

/** One connection: its name, when it was allowed and last used, and Revoke; once Revoke is pressed, the question in its place. */
export function ConnectionRow(p: { connection: ConnectionDto; asking: boolean; busy: boolean; onAsk: () => void; onKeep: () => void; onRevoke: () => void }) {
  const c = p.connection;
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 border-b border-rule py-2">
      {p.asking ? (
        <>
          <p className="py-1">{`Revoke ${c.clientName}? It stops reading at once.`}</p>
          <div className="flex items-center gap-x-6">
            <Button disabled={p.busy} onClick={p.onRevoke}>
              Revoke
            </Button>
            <Button tone="quiet" disabled={p.busy} onClick={p.onKeep}>
              Keep it
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="min-w-0 py-1">
            <p>{c.clientName}</p>
            <p className="meta">{`Connected ${dateWords(c.createdAt, 'dayOrDate')} · last used ${c.lastUsedAt ? dateWords(c.lastUsedAt, 'dayOrDate') : 'never'}`}</p>
          </div>
          <Button tone="danger" disabled={p.busy} onClick={p.onAsk}>
            Revoke
          </Button>
        </>
      )}
    </div>
  );
}

/** With no connection yet: the connector's address, with a way to copy it, and where it goes in claude.ai. */
function AddToClaude({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  const shown = useRef<HTMLElement>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard here (an insecure address, a refusal): select the address, ready to copy by hand.
      setCopied(false);
      const text = shown.current;
      if (!text) return;
      const range = document.createRange();
      range.selectNodeContents(text);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };
  return (
    <div>
      <p className="meta">Add the portal to Claude as a custom connector, with this address:</p>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        <code ref={shown} className="num min-w-0 break-all rounded-[2px] border border-rule bg-sheet px-3 py-2 text-[0.8125rem]">
          {address}
        </code>
        <Button tone="secondary" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="meta mt-2">In claude.ai: Settings → Connectors → Add custom connector. You allow it here once.</p>
    </div>
  );
}
